import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Divider, Drawer, Group, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { leaseTractInputSchema, type LeaseTractInputSchema } from '../../../lib/schemas';
import { useCreateLeaseTract, useUpdateLeaseTract } from '../../../queries/lease-tracts';
import { useLeaseTractFormLayout } from '../../../queries/lease-tract-form-layout';
import { useProjectMembers } from '../../../queries/project-members';
import { useProjectStore } from '../../../project/project-store';
import { useAuthStore } from '../../../auth/auth-store';
import {
  LEASE_TRACT_FORM_FIELDS,
  LEASE_TRACT_FORM_FIELD_NAMES,
  LEASE_TRACT_DEFAULT_LAYOUT,
  type LeaseTractFormContext,
} from '../../../lib/form-layout/lease-tract-fields';
import { FormLayoutRenderer } from '../../../lib/form-layout/FormLayoutRenderer';
import { SharedAcreageSection } from '../../../lib/SharedAcreageSection';
import { ApiError } from '../../../lib/api-error';
import type { LeaseTract } from '../../../types/api';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** The lease this tract belongs to (or will belong to, for a new tract). */
  leaseId: string;
  tract?: LeaseTract | null;
  onSaved?: (t: LeaseTract) => void;
}

const EMPTY: LeaseTractInputSchema = {
  agreement_number: '',
  type: null,
  status: null,
  reviewer_user_id: null,

  lessor: null,
  lessee: null,

  agreement_date: null,
  expiration_date: null,

  book: null,
  page: null,
  county: null,
  state: null,

  legal_description: null,
  legal_remark: null,
  all_depths: null,

  gross_acres: null,
  net_acres: null,

  royalty: null,
  orri_owner: null,
  orri_decimal: null,
  wi_owner: null,
  wi_decimal: null,
  other_burdens: null,
};

/**
 * node-postgres returns NUMERIC columns (gross_acres/net_acres,
 * royalty/orri_decimal/wi_decimal) as strings to avoid precision loss —
 * convert them back to JS numbers here so zodResolver's z.number() checks
 * pass on save. Mirrors WellFormDrawer.tsx's fromWell() / LeaseFormDrawer.tsx's
 * fromLease().
 */
function num(v: string | number | null | undefined): number | null {
  return v === null || v === undefined ? null : typeof v === 'string' ? Number(v) : v;
}

function fromTract(t: LeaseTract): LeaseTractInputSchema {
  return {
    agreement_number: t.agreement_number,
    type: t.type,
    status: t.status,
    reviewer_user_id: t.reviewer_user_id,

    lessor: t.lessor,
    lessee: t.lessee,

    agreement_date: t.agreement_date,
    expiration_date: t.expiration_date,

    book: t.book,
    page: t.page,
    county: t.county,
    state: t.state,

    legal_description: t.legal_description,
    legal_remark: t.legal_remark,
    all_depths: t.all_depths,

    gross_acres: num(t.gross_acres),
    net_acres: num(t.net_acres),

    royalty: num(t.royalty),
    orri_owner: t.orri_owner,
    orri_decimal: num(t.orri_decimal),
    wi_owner: t.wi_owner,
    wi_decimal: num(t.wi_decimal),
    other_burdens: t.other_burdens,
  };
}

/**
 * Form-layout drawer for a single lease's tract. Opened FROM
 * LeaseFormDrawer (via LeaseTractSection) — it stacks above the lease
 * drawer, so it renders with a higher `zIndex` than Mantine's default
 * Drawer/Overlay stack (200) to stay usable on top of it.
 */
const STACKED_DRAWER_Z_INDEX = 400;

export function TractFormDrawer({ opened, onClose, leaseId, tract, onSaved }: Props) {
  const isEdit = !!tract;
  const createM = useCreateLeaseTract();
  const updateM = useUpdateLeaseTract();
  const projectId = useProjectStore((s) => s.projectId);
  const role = useProjectStore((s) => s.projectRole);

  const layoutQ = useLeaseTractFormLayout(projectId);
  const layout = layoutQ.data?.layout ?? LEASE_TRACT_DEFAULT_LAYOUT;

  // Project members double as the reviewer_user_id dropdown's option list
  // AND the "users lookup" for resolving the display name behind
  // updated_by_user_id — /admin/users is superadmin-gated, but any project
  // member can see the membership roster.
  const { data: members } = useProjectMembers(projectId);
  const currentUser = useAuthStore((s) => s.user);
  // Server-enforced (spec §2.1: identical to wells): owner/superadmin may
  // assign any project member as reviewer; moderator/editor may only assign
  // themselves. The UI mirrors this to steer users away from an obvious
  // 403, not to replace it.
  const canAssignAnyReviewer = role === 'owner' || currentUser?.system_role === 'superadmin';
  const formContext: LeaseTractFormContext = {
    members: (members ?? []).map((m) => ({ value: m.user_id, label: m.username })),
    canAssignAnyReviewer,
    currentUserId: currentUser?.id ?? '',
  };

  const updatedByName = tract?.updated_by_user_id
    ? (members?.find((m) => m.user_id === tract.updated_by_user_id)?.username ??
      tract.updated_by_user_id)
    : null;

  const [lastOpenKey, setLastOpenKey] = useState<string | null>(null);

  const form = useForm<LeaseTractInputSchema>({
    resolver: zodResolver(leaseTractInputSchema),
    defaultValues: EMPTY,
  });

  const openKey = opened ? `${tract?.id ?? 'new'}-${tract?.updated_at ?? ''}` : null;
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (opened) form.reset(tract ? fromTract(tract) : EMPTY);
  }

  async function onSubmit(values: LeaseTractInputSchema) {
    try {
      if (isEdit && tract) {
        const res = await updateM.mutateAsync({ id: tract.id, patch: values });
        notifications.show({
          title: 'Tract saved',
          message: res.lease_tract.agreement_number,
          color: 'pine',
        });
        onSaved?.(res.lease_tract);
        onClose();
      } else {
        const res = await createM.mutateAsync({ ...values, lease_id: leaseId });
        notifications.show({
          title: 'Tract created',
          message: res.lease_tract.agreement_number,
          color: 'pine',
        });
        onSaved?.(res.lease_tract);
        onClose();
      }
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      padding="md"
      zIndex={STACKED_DRAWER_Z_INDEX}
      title={<Title order={4}>{isEdit ? 'Edit tract' : 'New tract'}</Title>}
      scrollAreaComponent={ScrollArea.Autosize}
      overlayProps={{ backgroundOpacity: 0.15, zIndex: STACKED_DRAWER_Z_INDEX }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="md">
          <FormLayoutRenderer
            layout={layout}
            control={form.control}
            register={form.register}
            registry={LEASE_TRACT_FORM_FIELDS}
            allNames={LEASE_TRACT_FORM_FIELD_NAMES}
            context={formContext}
            isEdit={isEdit}
          />

          <SharedAcreageSection tractId={tract?.id ?? null} />

          {isEdit && tract && (
            <>
              <Divider />
              <Text size="sm" c="dimmed">
                {`Last updated by ${updatedByName ?? '—'} on ${new Date(
                  tract.updated_at,
                ).toLocaleDateString()}`}
              </Text>
            </>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {isEdit ? 'Save changes' : 'Create tract'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
